import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { resolveFileUrl } from '../../utils/fileUrl';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import PaginationBar from '../../components/PaginationBar';
import {
  Users, Search, Check, X, Trash2, Eye, Download, Filter,
  Briefcase, MapPin, Phone, Mail, Building, User, ChevronDown, KeyRound, Edit, Save, FileText, Image
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ProfessionalManagement = () => {
  const [professionals, setProfessionals] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    profession: '',
    search: '',
    state: '',
    city: '',
    nukh: '',
    qualification: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProfessionals, setTotalProfessionals] = useState(0);
  const PAGE_SIZE = 50;
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    professions: [],
    nukhs: [],
    states: [],
    qualifications: [],
    citiesByState: {}
  });
  
  // Reset Password states
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Edit states
  const [editingProfessional, setEditingProfessional] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);

  // Admin Comments states
  const [adminComments, setAdminComments] = useState('');
  const [sendCommentEmail, setSendCommentEmail] = useState(false);
  const [savingComments, setSavingComments] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    setCurrentPage(1);
    fetchProfessionals(1);
    fetchStats();
  }, [filters.status, filters.profession, filters.state, filters.city, filters.nukh, filters.qualification]);

  // Fetch filter options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/professionals/options`);
        if (response.ok) {
          const data = await response.json();
          setFilterOptions({
            professions: data.professions || [],
            nukhs: data.nukhs || [],
            states: data.states || [],
            qualifications: data.qualifications || [],
            citiesByState: data.citiesByState || {}
          });
        }
      } catch (e) { console.error('Error fetching options:', e); }
    };
    fetchOptions();
  }, []);

  const fetchProfessionals = async (page = currentPage) => {
    setLoading(true);
    try {
      const skip = (page - 1) * PAGE_SIZE;
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.profession) params.append('profession', filters.profession);
      if (filters.search) params.append('search', filters.search);
      if (filters.state) params.append('state', filters.state);
      if (filters.city) params.append('city', filters.city);
      if (filters.nukh) params.append('nukh', filters.nukh);
      if (filters.qualification) params.append('qualification', filters.qualification);
      params.append('limit', PAGE_SIZE.toString());
      params.append('skip', skip.toString());

      const response = await fetch(`${BACKEND_URL}/api/professionals?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Handle both old format (array) and new format (object with professionals key)
        if (Array.isArray(data)) {
          // Old format - array of professionals
          setProfessionals(data);
          setTotalProfessionals(data.length);
        } else {
          // New format - object with professionals array
          setProfessionals(data.professionals || []);
          setTotalProfessionals(data.total || 0);
        }
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Error fetching professionals:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalProfessionals / PAGE_SIZE) || 1;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      fetchProfessionals(page);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/professionals/stats/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/professionals/${id}/status?status=${status}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`Professional ${status}`);
        fetchProfessionals();
        fetchStats();
        setShowModal(false);
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const handleSaveComments = async () => {
    if (!selectedProfessional) return;
    setSavingComments(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/professionals/${selectedProfessional.id}/comments`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminComments, sendEmail: sendCommentEmail })
      });
      if (response.ok) {
        toast.success(sendCommentEmail ? 'Comments saved & email sent' : 'Comments saved');
        fetchProfessionals();
      } else {
        toast.error('Failed to save comments');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setSavingComments(false);
    }
  };

  const deleteProfessional = async (id) => {
    if (!window.confirm('Are you sure you want to delete this professional?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/professionals/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Professional deleted');
        fetchProfessionals();
        fetchStats();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: resetPasswordModal.email,
          userType: 'professional',
          newPassword: newPassword
        })
      });
      
      if (response.ok) {
        toast.success(`Password reset successfully for ${resetPasswordModal.fullName}`);
        setResetPasswordModal(null);
        setNewPassword('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const openEditModal = (professional) => {
    setEditingProfessional(professional);
    setEditFormData({
      fullName: professional.fullName || '',
      fatherName: professional.fatherName || '',
      motherName: professional.motherName || '',
      nukh: professional.nukh || '',
      dateOfBirth: professional.dateOfBirth || '',
      email: professional.email || '',
      phone: professional.phone || '',
      whatsapp: professional.whatsapp || '',
      country: professional.country || 'India',
      state: professional.state || '',
      city: professional.city || '',
      currentPlace: professional.currentPlace || '',
      pincode: professional.pincode || '',
      address: professional.address || '',
      nativeVillage: professional.nativeVillage || '',
      nativeCity: professional.nativeCity || '',
      nativeState: professional.nativeState || '',
      nativeCountry: professional.nativeCountry || '',
      profession: professional.profession || '',
      professionOther: professional.professionOther || '',
      specialization: professional.specialization || '',
      qualification: professional.qualification || '',
      experienceYears: professional.experienceYears || '',
      organization: professional.organization || '',
      designation: professional.designation || '',
      officeAddress: professional.officeAddress || '',
      bio: professional.bio || '',
      website: professional.website || '',
      linkedIn: professional.linkedIn || '',
      interestedInConclave2026: professional.interestedInConclave2026 || false,
      status: professional.status || 'pending'
    });
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const saveProfessionalEdit = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/professionals/${editingProfessional.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editFormData)
      });
      
      if (response.ok) {
        toast.success('Professional updated successfully');
        setEditingProfessional(null);
        fetchProfessionals();
        fetchStats();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to update professional');
      }
    } catch (error) {
      toast.error('An error occurred while updating');
    } finally {
      setSaving(false);
    }
  };

  const [exporting, setExporting] = useState(false);
  const exportToExcel = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      // Fetch ALL records in batches
      let allProfessionals = [];
      let skip = 0;
      const batchSize = 200;
      let hasMore = true;
      while (hasMore) {
        const res = await fetch(`${BACKEND_URL}/api/professionals?skip=${skip}&limit=${batchSize}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) { toast.error('Failed to fetch data'); break; }
        const data = await res.json();
        const batch = data.professionals || [];
        allProfessionals = allProfessionals.concat(batch);
        if (batch.length < batchSize) { hasMore = false; }
        else { skip += batchSize; }
      }
      if (!allProfessionals.length) { toast.error('No data to export'); setExporting(false); return; }

      const rows = allProfessionals.map(p => ({
        'Professional ID': p.professionalId || '',
        'Full Name': p.fullName || '',
        'Father Name': p.fatherName || '',
        'Mother Name': p.motherName || '',
        'Gender': p.gender || '',
        'Date of Birth': p.dateOfBirth || '',
        'Nukh': p.nukh || '',
        'Email': p.email || '',
        'Phone': p.phone || '',
        'WhatsApp': p.whatsapp || '',
        'Profession': p.profession || '',
        'Profession (Other)': p.professionOther || '',
        'Specialization': p.specialization || '',
        'Qualification': p.qualification || '',
        'Designation': p.designation || '',
        'Organization': p.organization || '',
        'Office Address': p.officeAddress || '',
        'Experience (Years)': p.experienceYears || '',
        'Current Place': p.currentPlace || '',
        'City': p.city || '',
        'State': p.state || '',
        'Country': p.country || '',
        'Pincode': p.pincode || '',
        'Native Place': p.nativePlace || '',
        'Native Village': p.nativeVillage || '',
        'Native City': p.nativeCity || '',
        'Native State': p.nativeState || '',
        'Native Country': p.nativeCountry || '',
        'Address': p.address || '',
        'Bio': p.bio || '',
        'Website': p.website || '',
        'LinkedIn': p.linkedIn || '',
        'Interested in Conclave 2026': p.interestedInConclave2026 || '',
        'Status': p.status || '',
        'Registration Date': p.registrationDate ? new Date(p.registrationDate).toLocaleDateString() : (p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''),
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Professionals');
      XLSX.writeFile(wb, `professionals_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(`Exported ${rows.length} professionals`);
    } catch (err) { console.error('Export error:', err); toast.error('Export failed: ' + (err.message || err)); }
    finally { setExporting(false); }
  };

  const getProfessionDisplay = (prof) => {
    if (prof.profession === 'Other / अन्य' && prof.professionOther) {
      return prof.professionOther;
    }
    return prof.profession?.split(' / ')[0] || prof.profession;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Professional Management</h1>
        <p className="text-gray-500">Manage professional directory registrations</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => setFilters(prev => ({ ...prev, status: '' }))}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-gray-500">Total</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => setFilters(prev => ({ ...prev, status: 'pending' }))}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className="text-sm text-gray-500">Pending</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => setFilters(prev => ({ ...prev, status: 'approved' }))}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.approved}</p>
              <p className="text-sm text-gray-500">Approved</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => setFilters(prev => ({ ...prev, status: 'rejected' }))}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <X className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.rejected}</p>
              <p className="text-sm text-gray-500">Rejected</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <Input
              type="text"
              placeholder="Search by name, email, phone..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="pl-10"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setCurrentPage(1);
                  fetchProfessionals(1);
                }
              }}
            />
          </div>
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} className="mr-2" />
            Filters
            <ChevronDown size={16} className={`ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>
          <Button onClick={exportToExcel} disabled={exporting}>
            <Download size={16} className="mr-2" />
            {exporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        </div>

        {showFilters && (
          <div className="bg-white rounded-lg p-4 border mb-4">
            <div className="flex flex-wrap gap-4">
              <div className="w-full sm:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full sm:w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  data-testid="prof-filter-status"
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div className="w-full sm:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">Profession</label>
                <select
                  value={filters.profession}
                  onChange={(e) => setFilters(prev => ({ ...prev, profession: e.target.value }))}
                  className="w-full sm:w-44 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  data-testid="prof-filter-profession"
                >
                  <option value="">All</option>
                  {filterOptions.professions.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div className="w-full sm:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select
                  value={filters.state}
                  onChange={(e) => setFilters(prev => ({ ...prev, state: e.target.value, city: '' }))}
                  className="w-full sm:w-44 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  data-testid="prof-filter-state"
                >
                  <option value="">All States</option>
                  {filterOptions.states.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="w-full sm:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <select
                  value={filters.city}
                  onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
                  className="w-full sm:w-44 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  data-testid="prof-filter-city"
                >
                  <option value="">All Cities</option>
                  {(filters.state && filterOptions.citiesByState[filters.state] 
                    ? filterOptions.citiesByState[filters.state] 
                    : []
                  ).map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="w-full sm:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nukh</label>
                <select
                  value={filters.nukh}
                  onChange={(e) => setFilters(prev => ({ ...prev, nukh: e.target.value }))}
                  className="w-full sm:w-44 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  data-testid="prof-filter-nukh"
                >
                  <option value="">All Nukh</option>
                  {filterOptions.nukhs.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              <div className="w-full sm:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">Qualification</label>
                <select
                  value={filters.qualification}
                  onChange={(e) => setFilters(prev => ({ ...prev, qualification: e.target.value }))}
                  className="w-full sm:w-44 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  data-testid="prof-filter-qualification"
                >
                  <option value="">All</option>
                  {filterOptions.qualifications.map(q => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
              </div>

              {(filters.status || filters.profession || filters.state || filters.city || filters.nukh || filters.qualification) && (
                <div className="flex items-end">
                  <Button variant="ghost" size="sm" onClick={() => setFilters({ status: '', profession: '', search: '', state: '', city: '', nukh: '', qualification: '' })}>
                    <X className="w-4 h-4 mr-1" /> Clear Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Professionals Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Professional</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profession</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  </td>
                </tr>
              ) : professionals.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    No professionals found
                  </td>
                </tr>
              ) : (
                professionals.map((prof) => (
                  <tr key={prof.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                          {prof.profilePhoto ? (
                            <img src={resolveFileUrl(prof.profilePhoto)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-5 h-5 text-primary" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{prof.fullName}</p>
                          {prof.nukh && <p className="text-xs text-gray-500">{prof.nukh}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        <p className="text-gray-900">{prof.email}</p>
                        <p className="text-gray-500">{prof.phone}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        <p className="font-medium text-primary">{getProfessionDisplay(prof)}</p>
                        {prof.specialization && <p className="text-gray-500">{prof.specialization}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-600">
                        {prof.city}, {prof.state}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {getStatusBadge(prof.status)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {new Date(prof.registrationDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { setSelectedProfessional(prof); setShowModal(true); setAdminComments(prof.adminComments || ''); setSendCommentEmail(false); }}
                          className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => setResetPasswordModal(prof)}
                          className="p-2 hover:bg-blue-100 rounded-lg text-blue-600"
                          title="Reset Password"
                        >
                          <KeyRound size={16} />
                        </button>
                        {prof.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateStatus(prof.id, 'approved')}
                              className="p-2 hover:bg-green-100 rounded-lg text-green-600"
                              title="Approve"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={() => updateStatus(prof.id, 'rejected')}
                              className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                              title="Reject"
                            >
                              <X size={16} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => openEditModal(prof)}
                          className="p-2 hover:bg-orange-100 rounded-lg text-orange-600"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => deleteProfessional(prof.id)}
                          className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t">
            <PaginationBar
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(p) => goToPage(p)}
              totalItems={totalProfessionals}
              pageSize={PAGE_SIZE}
              itemLabel="professionals"
              testIdPrefix="admin-professionals"
            />
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      {showModal && selectedProfessional && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold">Professional Details</h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                  {selectedProfessional.profilePhoto ? (
                    <img src={resolveFileUrl(selectedProfessional.profilePhoto)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-10 h-10 text-primary" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold">{selectedProfessional.fullName}</h2>
                  <p className="text-primary font-medium">{getProfessionDisplay(selectedProfessional)}</p>
                  {selectedProfessional.professionalId && (
                    <p className="text-sm text-gray-500">ID: {selectedProfessional.professionalId}</p>
                  )}
                  {getStatusBadge(selectedProfessional.status)}
                </div>
              </div>

              {/* Personal Details */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Personal Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">Full Name</p>
                    <p>{selectedProfessional.fullName}</p>
                  </div>
                  {selectedProfessional.fatherName && (
                    <div>
                      <p className="text-xs text-gray-400">Father's Name</p>
                      <p>{selectedProfessional.fatherName}</p>
                    </div>
                  )}
                  {selectedProfessional.motherName && (
                    <div>
                      <p className="text-xs text-gray-400">Mother's / Husband's Name</p>
                      <p>{selectedProfessional.motherName}</p>
                    </div>
                  )}
                  {selectedProfessional.nukh && (
                    <div>
                      <p className="text-xs text-gray-400">Nukh</p>
                      <p>{selectedProfessional.nukh}</p>
                    </div>
                  )}
                  {selectedProfessional.dateOfBirth && (
                    <div>
                      <p className="text-xs text-gray-400">Date of Birth</p>
                      <p>{selectedProfessional.dateOfBirth}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Details */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Contact Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">Email</p>
                    <p>{selectedProfessional.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Phone</p>
                    <p>{selectedProfessional.phone}</p>
                  </div>
                  {selectedProfessional.whatsapp && (
                    <div>
                      <p className="text-xs text-gray-400">WhatsApp</p>
                      <p>{selectedProfessional.whatsapp}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Location Details */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Location Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">City</p>
                    <p>{selectedProfessional.city}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">State</p>
                    <p>{selectedProfessional.state}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Country</p>
                    <p>{selectedProfessional.country || 'India'}</p>
                  </div>
                  {selectedProfessional.currentPlace && (
                    <div>
                      <p className="text-xs text-gray-400">Current Place</p>
                      <p>{selectedProfessional.currentPlace}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-400">Native Place / Village</p>
                    <p>{selectedProfessional.nativeVillage || selectedProfessional.nativePlace || <span className="text-gray-300 italic">Not provided</span>}</p>
                  </div>
                  {selectedProfessional.nativeCity && (
                    <div>
                      <p className="text-xs text-gray-400">Native City</p>
                      <p>{selectedProfessional.nativeCity}</p>
                    </div>
                  )}
                  {selectedProfessional.nativeState && (
                    <div>
                      <p className="text-xs text-gray-400">Native State</p>
                      <p>{selectedProfessional.nativeState}</p>
                    </div>
                  )}
                  {selectedProfessional.nativeCountry && (
                    <div>
                      <p className="text-xs text-gray-400">Native Country</p>
                      <p>{selectedProfessional.nativeCountry}</p>
                    </div>
                  )}
                  {selectedProfessional.pincode && (
                    <div>
                      <p className="text-xs text-gray-400">Pincode</p>
                      <p>{selectedProfessional.pincode}</p>
                    </div>
                  )}
                  {selectedProfessional.address && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-400">Address</p>
                      <p>{selectedProfessional.address}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Professional Details */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Professional Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">Profession</p>
                    <p>{getProfessionDisplay(selectedProfessional)}</p>
                  </div>
                  {selectedProfessional.specialization && (
                    <div>
                      <p className="text-xs text-gray-400">Specialization</p>
                      <p>{selectedProfessional.specialization}</p>
                    </div>
                  )}
                  {selectedProfessional.qualification && (
                    <div>
                      <p className="text-xs text-gray-400">Qualification</p>
                      <p>{selectedProfessional.qualification}</p>
                    </div>
                  )}
                  {selectedProfessional.experienceYears && (
                    <div>
                      <p className="text-xs text-gray-400">Experience</p>
                      <p>{selectedProfessional.experienceYears} years</p>
                    </div>
                  )}
                  {selectedProfessional.organization && (
                    <div>
                      <p className="text-xs text-gray-400">Organization</p>
                      <p>{selectedProfessional.organization}</p>
                    </div>
                  )}
                  {selectedProfessional.designation && (
                    <div>
                      <p className="text-xs text-gray-400">Designation</p>
                      <p>{selectedProfessional.designation}</p>
                    </div>
                  )}
                  {selectedProfessional.officeAddress && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-400">Office Address</p>
                      <p>{selectedProfessional.officeAddress}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Social & Profile */}
              {(selectedProfessional.bio || selectedProfessional.website || selectedProfessional.linkedIn) && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Profile & Social</h4>
                  <div className="space-y-3">
                    {selectedProfessional.bio && (
                      <div>
                        <p className="text-xs text-gray-400 mb-1">Bio</p>
                        <p className="text-gray-700">{selectedProfessional.bio}</p>
                      </div>
                    )}
                    {selectedProfessional.website && (
                      <div>
                        <p className="text-xs text-gray-400">Website</p>
                        <a href={selectedProfessional.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          {selectedProfessional.website}
                        </a>
                      </div>
                    )}
                    {selectedProfessional.linkedIn && (
                      <div>
                        <p className="text-xs text-gray-400">LinkedIn</p>
                        <a href={selectedProfessional.linkedIn} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          {selectedProfessional.linkedIn}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Info / Feedback from Applicant */}
              {selectedProfessional.additionalInfo && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Additional Info / Feedback from Applicant</h4>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900 whitespace-pre-wrap">{selectedProfessional.additionalInfo}</p>
                  </div>
                </div>
              )}

              {/* Documents Section */}
              {selectedProfessional.profilePhoto && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Uploaded Documents
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex flex-wrap gap-3">
                      <a
                        href={resolveFileUrl(selectedProfessional.profilePhoto)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                        data-testid="view-professional-photo"
                      >
                        <Image size={16} />
                        View Profile Photo
                      </a>
                      <a
                        href={resolveFileUrl(selectedProfessional.profilePhoto)}
                        download
                        className="inline-flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
                        data-testid="download-professional-photo"
                      >
                        <Download size={16} />
                        Download
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Conclave Interest */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Additional Info</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">Interested in Conclave 2026</p>
                    <p>{selectedProfessional.interestedInConclave2026 ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Registration Date</p>
                    <p>{new Date(selectedProfessional.registrationDate).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Admin Comments / Feedback */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Admin Comments / Feedback</h4>
                <textarea
                  className="w-full border rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
                  rows={3}
                  placeholder="Write your feedback or suggestions here... / अपनी टिप्पणी या सुझाव यहाँ लिखें..."
                  value={adminComments}
                  onChange={(e) => setAdminComments(e.target.value)}
                  data-testid="prof-admin-comments-input"
                />
                <div className="flex items-center gap-3 mt-2">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={sendCommentEmail}
                      onChange={(e) => setSendCommentEmail(e.target.checked)}
                      data-testid="prof-send-comment-email"
                    />
                    Send email notification
                  </label>
                  <Button
                    size="sm"
                    onClick={handleSaveComments}
                    disabled={savingComments}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="prof-save-comments-btn"
                  >
                    {savingComments ? 'Saving...' : 'Save Comments'}
                  </Button>
                </div>
              </div>

              {selectedProfessional.status === 'pending' && (
                <div className="flex gap-4 pt-4 border-t">
                  <Button
                    onClick={() => updateStatus(selectedProfessional.id, 'approved')}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <Check size={16} className="mr-2" /> Approve
                  </Button>
                  <Button
                    onClick={() => updateStatus(selectedProfessional.id, 'rejected')}
                    variant="destructive"
                    className="flex-1"
                  >
                    <X size={16} className="mr-2" /> Reject
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResetPasswordModal(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <KeyRound className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="font-bold text-xl text-gray-900">Reset Password</h2>
                <p className="text-sm text-gray-500">पासवर्ड रीसेट करें</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-600 mb-1">Resetting password for:</p>
              <p className="font-semibold text-gray-900">{resetPasswordModal.fullName}</p>
              <p className="text-sm text-gray-500">{resetPasswordModal.email}</p>
            </div>

            <form onSubmit={resetPassword} className="space-y-4">
              <div>
                <Label htmlFor="newProfPassword">New Password / नया पासवर्ड</Label>
                <Input
                  id="newProfPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  required
                  minLength={6}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setResetPasswordModal(null);
                    setNewPassword('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={resettingPassword}
                >
                  {resettingPassword ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Professional Modal */}
      {editingProfessional && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditingProfessional(null)}>
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <Edit className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h2 className="font-bold text-xl text-gray-900">Edit Professional</h2>
                  <p className="text-sm text-gray-500">पेशेवर संपादित करें</p>
                </div>
              </div>
              <button onClick={() => setEditingProfessional(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            {/* Photo Upload Section */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">Profile Photo / प्रोफ़ाइल फ़ोटो</Label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-white border flex items-center justify-center overflow-hidden">
                  {editingProfessional.profilePhoto ? (
                    <img src={resolveFileUrl(editingProfessional.profilePhoto)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-10 h-10 text-gray-300" />
                  )}
                </div>
                <div>
                  <input
                    id="admin-prof-photo-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) { toast.error('Photo must be under 5MB'); return; }
                      const fd = new FormData();
                      fd.append('photo', file);
                      try {
                        const res = await fetch(`${BACKEND_URL}/api/professionals/${editingProfessional.id}/photo`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` },
                          body: fd
                        });
                        if (res.ok) {
                          const data = await res.json();
                          setEditingProfessional(prev => ({ ...prev, profilePhoto: data.photoUrl }));
                          fetchProfessionals();
                          toast.success('Photo uploaded successfully');
                        } else {
                          toast.error('Failed to upload photo');
                        }
                      } catch (err) {
                        toast.error('Photo upload failed');
                      } finally {
                        e.target.value = '';
                      }
                    }}
                    data-testid="admin-prof-photo-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('admin-prof-photo-input').click()}
                    data-testid="admin-prof-photo-upload-btn"
                  >
                    <Image size={14} className="mr-2" />
                    {editingProfessional.profilePhoto ? 'Replace Photo' : 'Upload Photo'}
                  </Button>
                  <p className="text-xs text-gray-500 mt-1">JPEG, PNG or WebP (max 5MB)</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label>Full Name / पूरा नाम</Label>
                <Input name="fullName" value={editFormData.fullName} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Father Name / पिता का नाम</Label>
                <Input name="fatherName" value={editFormData.fatherName} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Mother's / Husband's Name / माता / पति का नाम</Label>
                <Input name="motherName" value={editFormData.motherName} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Nukh / नुख</Label>
                <Input name="nukh" value={editFormData.nukh} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Date of Birth / जन्म तिथि</Label>
                <Input name="dateOfBirth" type="date" value={editFormData.dateOfBirth} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Email / ईमेल</Label>
                <Input name="email" type="email" value={editFormData.email} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Phone / फ़ोन</Label>
                <Input name="phone" value={editFormData.phone} onChange={handleEditChange} />
              </div>
              <div>
                <Label>WhatsApp / व्हाट्सएप</Label>
                <Input name="whatsapp" value={editFormData.whatsapp} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Country / देश</Label>
                <Input name="country" value={editFormData.country} onChange={handleEditChange} />
              </div>
              <div>
                <Label>State / राज्य</Label>
                <Input name="state" value={editFormData.state} onChange={handleEditChange} />
              </div>
              <div>
                <Label>City / शहर</Label>
                <Input name="city" value={editFormData.city} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Pincode / पिनकोड</Label>
                <Input name="pincode" value={editFormData.pincode} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Area / Locality / क्षेत्र</Label>
                <Input name="currentPlace" value={editFormData.currentPlace} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Address / पता</Label>
                <Input name="address" value={editFormData.address} onChange={handleEditChange} />
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <Label className="text-sm font-semibold text-gray-600 border-b pb-1 mb-2 block">Native Location / मूल स्थान</Label>
              </div>
              <div>
                <Label>Native Village / गाँव</Label>
                <Input name="nativeVillage" value={editFormData.nativeVillage} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Native City / शहर</Label>
                <Input name="nativeCity" value={editFormData.nativeCity} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Native State / राज्य</Label>
                <Input name="nativeState" value={editFormData.nativeState} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Native Country / देश</Label>
                <Input name="nativeCountry" value={editFormData.nativeCountry} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Profession / पेशा</Label>
                <Input name="profession" value={editFormData.profession} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Specialization / विशेषज्ञता</Label>
                <Input name="specialization" value={editFormData.specialization} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Qualification / योग्यता</Label>
                <Input name="qualification" value={editFormData.qualification} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Experience (Years) / अनुभव (वर्ष)</Label>
                <Input name="experienceYears" value={editFormData.experienceYears} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Organization / संस्था</Label>
                <Input name="organization" value={editFormData.organization} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Designation / पद</Label>
                <Input name="designation" value={editFormData.designation} onChange={handleEditChange} />
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <Label>Office Address / कार्यालय पता</Label>
                <textarea 
                  name="officeAddress" 
                  value={editFormData.officeAddress} 
                  onChange={handleEditChange} 
                  rows={2}
                  className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm resize-none"
                />
              </div>
              <div>
                <Label>Website / वेबसाइट</Label>
                <Input name="website" value={editFormData.website} onChange={handleEditChange} />
              </div>
              <div>
                <Label>LinkedIn / लिंक्डइन</Label>
                <Input name="linkedIn" value={editFormData.linkedIn} onChange={handleEditChange} />
              </div>
              <div>
                <Label>Status / स्थिति</Label>
                <select name="status" value={editFormData.status} onChange={handleEditChange} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="interestedInConclave2026"
                  checked={editFormData.interestedInConclave2026}
                  onChange={handleEditChange}
                  className="w-4 h-4"
                />
                <Label>Interested in Conclave 2026</Label>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <Label>Bio / परिचय</Label>
                <textarea 
                  name="bio" 
                  value={editFormData.bio} 
                  onChange={handleEditChange} 
                  rows={3}
                  className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditingProfessional(null)}>
                Cancel
              </Button>
              <Button onClick={saveProfessionalEdit} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalManagement;
